import { create } from 'zustand';
import { useAuthStore } from '@/stores/auth-store';

/**
 * "Recent" section of the global-search dropdown — the last few things the
 * user actually opened FROM search (an entity row, or a "Go to" quick-nav
 * row). Purely client-side: there is no backend view-history capability, and
 * this needs none. Persisted to localStorage, keyed per user, following the
 * same convention as the other per-feature preference stores.
 *
 * Scope note: only search-originated opens are recorded. Opening a screen
 * from the Screens page does NOT land here — extending to app-wide visits
 * would mean instrumenting every native feature page.
 */

/** Rows the panel shows. Older entries fall off the end. */
export const MAX_RECENT_ITEMS = 8;

const STORAGE_PREFIX = 'scio3-search-recents';

/**
 * Only the fields `useSearchItemNav` + the dropdown's row renderer read back.
 * Deliberately a whitelist rather than the whole GraphQL object — keeps the
 * localStorage payload small and stops `__typename` / thumbnail blobs from
 * being persisted.
 */
const PERSISTED_ITEM_KEYS = [
  '_id',
  'name',
  'deviceName',
  'originalFileName',
  'appType',
  'type',
  'path',
  'teamId',
  'teamName',
  'userId',
  'email',
  'memberCount',
  'author',
  'parentGroupInfo',
] as const;

interface RecentEntityEntry {
  kind: 'entity';
  /** Dedup + React key. */
  key: string;
  /** One of `GROUP_ORDER` — drives the icon, label and select handler. */
  entityType: string;
  /** Slimmed search-result payload, replayed through `selectItem`. */
  item: Record<string, unknown>;
  /** Epoch ms of the most recent open. */
  at: number;
}

interface RecentNavEntry {
  kind: 'nav';
  key: string;
  /** `NavTarget.id` — resolved against `useVisibleNavTargets()` at render
   *  time so a since-revoked permission drops the row instead of rendering
   *  a link the user can no longer follow. */
  navTargetId: string;
  at: number;
}

export type RecentSearchEntry = RecentEntityEntry | RecentNavEntry;

interface SearchRecentsState {
  /** User id the in-memory list was hydrated for; '' before the first hydrate. */
  hydratedFor: string;
  items: RecentSearchEntry[];
  /** Re-read the signed-in user's list from localStorage. Idempotent — safe to
   *  call on every panel open, which is how account switches get picked up. */
  hydrate: () => void;
  recordEntity: (entityType: string, item: Record<string, unknown>) => void;
  recordNavTarget: (navTargetId: string) => void;
  clear: () => void;
}

function currentUserId(): string {
  return useAuthStore.getState().me?._id ?? '';
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

function read(userId: string): RecentSearchEntry[] {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentEntry).slice(0, MAX_RECENT_ITEMS);
  } catch {
    // Corrupt/unavailable storage (private mode, quota, hand-edited value) —
    // treat as "no recents" rather than breaking the whole search panel.
    return [];
  }
}

function write(userId: string, items: RecentSearchEntry[]): void {
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(items));
  } catch {
    // Quota / private-mode write failure — the in-memory list still works for
    // this session.
  }
}

/** Structural guard so a stale or hand-edited payload can't crash the render. */
function isRecentEntry(value: unknown): value is RecentSearchEntry {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Record<string, unknown>;
  if (typeof entry.key !== 'string' || typeof entry.at !== 'number') return false;
  if (entry.kind === 'nav') return typeof entry.navTargetId === 'string';
  if (entry.kind === 'entity') {
    return (
      typeof entry.entityType === 'string' &&
      typeof entry.item === 'object' &&
      entry.item !== null &&
      typeof (entry.item as Record<string, unknown>)._id === 'string'
    );
  }
  return false;
}

export function slimSearchItem(item: Record<string, unknown>): Record<string, unknown> {
  const slim: Record<string, unknown> = {};
  for (const key of PERSISTED_ITEM_KEYS) {
    if (item[key] !== undefined) slim[key] = item[key];
  }
  return slim;
}

/**
 * Members repeat per team-pairing, so `_id` alone isn't unique — mirrors the
 * same reasoning as `getRowKey` in `utils/entity-types.ts`.
 */
export function getRecentEntityKey(entityType: string, item: Record<string, unknown>): string {
  if (entityType === 'members') {
    const uid = (item.userId as string) || (item.email as string) || '';
    return `entity:members:${item.teamId as string}:${uid}`;
  }
  return `entity:${entityType}:${item._id as string}`;
}

/** Newest first, deduped by `key`, capped at `MAX_RECENT_ITEMS`. */
function prepend(items: RecentSearchEntry[], entry: RecentSearchEntry): RecentSearchEntry[] {
  return [entry, ...items.filter((existing) => existing.key !== entry.key)].slice(
    0,
    MAX_RECENT_ITEMS,
  );
}

export const useSearchRecentsStore = create<SearchRecentsState>((set, get) => ({
  hydratedFor: '',
  items: [],

  hydrate: () => {
    const userId = currentUserId();
    if (get().hydratedFor === userId) return;
    set({ hydratedFor: userId, items: read(userId) });
  },

  recordEntity: (entityType, item) => {
    const userId = currentUserId();
    if (!userId || typeof item._id !== 'string') return;
    const entry: RecentEntityEntry = {
      kind: 'entity',
      key: getRecentEntityKey(entityType, item),
      entityType,
      item: slimSearchItem(item),
      at: Date.now(),
    };
    // Read-then-write against storage rather than in-memory state: `record` can
    // fire before the panel ever hydrated (e.g. a row click on the search page
    // in a fresh tab), and blindly writing `[entry]` would wipe the list.
    const next = prepend(get().hydratedFor === userId ? get().items : read(userId), entry);
    write(userId, next);
    set({ hydratedFor: userId, items: next });
  },

  recordNavTarget: (navTargetId) => {
    const userId = currentUserId();
    if (!userId) return;
    const entry: RecentNavEntry = {
      kind: 'nav',
      key: `nav:${navTargetId}`,
      navTargetId,
      at: Date.now(),
    };
    const next = prepend(get().hydratedFor === userId ? get().items : read(userId), entry);
    write(userId, next);
    set({ hydratedFor: userId, items: next });
  },

  clear: () => {
    const userId = currentUserId();
    if (userId) {
      try {
        localStorage.removeItem(storageKey(userId));
      } catch {
        // Nothing to recover from — the in-memory reset below still applies.
      }
    }
    set({ hydratedFor: userId, items: [] });
  },
}));
